<div class="container-fluid" *ngIf="!loading && product">

	<div class="d-flex justify-content-between align-items-center mb-3">
		<div>
			<h3 class="mb-0">{{ product.name }}</h3>
			<small class="text-muted">
				{{ product.productType }}
			</small>
		</div>

		<div>
			<a nz-button nzType="default" [routerLink]="['/product', product!.id, 'edit']">
				Edit
			</a>
		</div>
	</div>

	<nz-tabset nzType="card" (nzSelectedIndexChange)="selectedTab=$event">
		<nz-tab nzTitle="Details">
			<div class="p-3">

				<!-- General Info -->
				<nz-card nzTitle="General Information" class="mb-3">
					<nz-descriptions nzBordered [nzColumn]="2">
						<nz-descriptions-item nzTitle="Name">{{ product.name }}</nz-descriptions-item>
						<nz-descriptions-item nzTitle="Product Type">
							<nz-tag nzColor="blue">{{ product.productType }}</nz-tag>
						</nz-descriptions-item>
						<nz-descriptions-item nzTitle="Benefit Type">
							<nz-tag nzColor="green">{{ product.benefitType }}</nz-tag>
						</nz-descriptions-item>
						<nz-descriptions-item nzTitle="Created At">{{ product.createdAt | date:'medium' }}
						</nz-descriptions-item>
						<nz-descriptions-item nzTitle="Created By">{{ product.createdByAgent?.username ?? '—' }}
						</nz-descriptions-item>
						<nz-descriptions-item nzTitle="Primary Premium Recipient">
							{{ product.primaryPremiumRecipient?.name ?? '—' }}
							({{ product.primaryPremiumRecipient?.type ?? '—' }})</nz-descriptions-item>
						<nz-descriptions-item nzTitle="Description" [nzSpan]="2">{{ product.description }}
						</nz-descriptions-item>
					</nz-descriptions>
				</nz-card>

				<!-- Lapse Settings -->
				<nz-card nzTitle="Lapse Settings" class="mb-3">
					<nz-descriptions nzBordered [nzColumn]="3">
						<nz-descriptions-item nzTitle="Lapsed Premium Window (months)">
							{{ product.lapsedPremiumWindowInMonths }}</nz-descriptions-item>
						<nz-descriptions-item nzTitle="Max Lapsed Premiums in Window">
							{{ product.maxAllowedLapsedPremiumsInWindowPeriod }}</nz-descriptions-item>
						<nz-descriptions-item nzTitle="Max Consecutive Lapsed Premiums">
							{{ product.maxAllowedConsecutiveLapsedPremiums }}</nz-descriptions-item>
					</nz-descriptions>
				</nz-card>

				<!-- Rules -->
				<nz-card nzTitle="Rules" class="mb-3" *ngIf="product.rules?.length">
					<nz-table #rulesTable [nzData]="product.rules" nzSize="small" [nzShowPagination]="false" nzFrontPagination="false">
						<thead>
							<tr>
								<th>Rule Type</th>
								<th>Applied To</th>
								<th>Threshold</th>
							</tr>
						</thead>
						<tbody>
							<tr *ngFor="let rule of rulesTable.data">
								<td>
									<nz-tag>{{ rule.ruleType }}</nz-tag>
								</td>
								<td>{{ rule.appliedTo }}</td>
								<td>{{ rule.threshold }}</td>
							</tr>
						</tbody>
					</nz-table>
				</nz-card>

				<!-- Offers -->
				<nz-card nzTitle="Offers" class="mb-3" *ngIf="product.offers?.length">
					<nz-table [nzData]="product.offers" nzSize="small" [nzShowPagination]="false" nzFrontPagination="false">
						<thead>
							<tr>
								<th>Offer</th>
								<th>Benefit</th>
								<th>Premium</th>
								<th *ngFor="let recipient of offerCommissionRecipients">{{ recipient }}</th>
							</tr>
						</thead>
						<tbody>
							<tr *ngFor="let offer of product.offers">
								<td>
									<strong>{{ offer.name }}</strong>
									<div class="text-muted" style="font-size: 12px;">{{ offer.description }}</div>
								</td>

								<!-- Premium column -->
								<td>
									<ng-container *ngIf="getOfferFixedPremium(offer) as fixed">
										<div>{{ fixed | number }} <small class="text-muted">fixed</small></div>
									</ng-container>
									<ng-container *ngIf="getOfferPercentagePremium(offer) as pct">
										<div>{{ pct }} <small class="text-muted">%</small></div>
									</ng-container>
								</td>

								<!-- Benefit column -->
								<td>
									<ng-container *ngIf="getOfferFixedBenefit(offer) as fixed">
										<div>{{ fixed | number }} <small class="text-muted">fixed</small></div>
									</ng-container>
									<ng-container *ngIf="getOfferPercentageBenefit(offer) as pct">
										<div>{{ pct }} <small class="text-muted">%</small></div>
									</ng-container>
								</td>

								<!-- One column per commission recipient -->
								<td *ngFor="let recipient of offerCommissionRecipients">
									<ng-container *ngIf="getOfferCommission(offer, recipient) as commission">
										{{ commission.amount | number }}
										<small class="text-muted">{{ commission.type === 'Percentage' ? '%' : 'fixed' }}</small>
									</ng-container>
									<ng-container *ngIf="!getOfferCommission(offer, recipient)">—</ng-container>
								</td>
							</tr>
						</tbody>
					</nz-table>
				</nz-card>

				<!-- Extended Benefits -->
				<nz-card nzTitle="Extended Benefits" class="mb-3" *ngIf="product.extendeds?.length">
					<nz-collapse>
						<nz-collapse-panel *ngFor="let ext of product.extendeds" [nzHeader]="ext.name">

							<p class="text-muted mb-3">{{ ext.description }}</p>

							<nz-table [nzData]="ext.details" nzSize="small" [nzShowPagination]="false" nzFrontPagination="false">
								<thead>
									<tr>
										<th>Effective Date</th>
										<th>Relation</th>
										<th>Age Range</th>
										<th>Premium Type</th>
										<th>Premium Amount</th>
										<th>Benefit Amount</th>
										<th>Commissions</th>
									</tr>
								</thead>
								<tbody>
									<tr *ngFor="let detail of ext.details">
										<td>{{ detail.fromDate | date:'shortDate' }} –
											{{ detail.toDate | date:'shortDate' }}</td>
										<td>{{ detail.relationToMainMember }}</td>
										<td>{{ detail.fromAge }} – {{ detail.toAge }}</td>
										<td>
											<nz-tag>{{ detail.premiumType }}</nz-tag>
										</td>
										<td>{{ detail.premiumAmount | number }}</td>
										<td>{{ detail.benefitAmount | number }}</td>
										<td>
											<ng-container *ngFor="let c of detail.commissions">
												<nz-tag nzColor="cyan">{{ c.recipient?.name }}:
													{{ c.commissionAmount }}{{ c.commissionType === 'Percentage' ? '%' : '' }}
												</nz-tag>
											</ng-container>
										</td>
									</tr>
								</tbody>
							</nz-table>

						</nz-collapse-panel>
					</nz-collapse>
				</nz-card>

			</div>
		</nz-tab>


		<nz-tab nzTitle="Policies" (nzSelect)="selectedPolicyTab()">
			<nz-card>
				<div class="row mt-3">
					<div class="col">
						<app-table
							[data]="policies"
							[headerClass]="'table-info'"
							[columns]="policyColumnRef"
							[filters]="policyFilters"
							[loading]="policyLoading"
							[disablePaging]="false"
							(onUpdateRequired)="doFetchPolicyResults($event)">
						</app-table>
					</div>
				</div>
			</nz-card>
		</nz-tab>
		<!-- <nz-tab nzTitle="Schedule" (nzSelect)="openSchedule()">
		</nz-tab> -->
	</nz-tabset>
	<div [hidden]="selectedTab != 2 && selectedTab != 3">
		<router-outlet ></router-outlet>
	</div>
</div>
